import React, {CSSProperties, useMemo} from 'react';
import {useSplitPaneResize} from '../../../hooks/SplitPaneResize';
import {Resizer} from './Resizer';
import {Pane} from './Pane';
import classnames from 'classnames';

/**
 * This is a low level component that is for internal use only.  It is used by VerticalSplitPanel
 * and HorizontalSplitPanel.
 *
 * This is a modified version of https://github.com/NeoRaider/react-multi-split-pane
 * MIT License
 */
export interface SplitPaneProps {

   /**
    * The direction of the resizer (a horizontal split panel will use a vertical resizer)
    */
   split?: 'horizontal' | 'vertical';

   /**
    * Array of default sizes for the individual panes. Missing values default to 1. When
    * no defaultSizes are passed, all sizes default to 1, equally distributing the available space
    * (as far as minSize values permit).  If the value is a string and ends with px, it is treated
    * as a pixel value rather than a flex value.  If the value is 'layout', it will try to use
    * the layout size as the default size.  Using pixel sizes and 'layout' generally require that
    * at least one of the default sizes is allowed to flex (i.e. is set to something like 1).
    */
   defaultSizes?: (string | 'layout' | number)[];

   /**
    * Minimum size of all panes (in pixels), or array containing individual minimum sizes for each
    * pane.
    * @defaultvalue 50
    */
   minSize?: number | number[];

   /**
    * Maximum size of all panes (in pixels), or array containing individual maximum sizes for each
    * pane.  A -1 indicates no maximum size
    * @defaultvalue -1
    */
   maxSize?: number | number[];

   /**
    * Used to temporarily disable resizing on a resizer index.  If there are two regions, there will
    * be only one resizer index, which starts at 0.
    */
   disableResizers?: number | number[];

   /**
    * Sets a className on the panel.
    */
   className?: string;

   /**
    * Sets styles on the panel.
    */
   style?: CSSProperties;

   /**
    * The classname for the resizer
    */
   resizerClassName?: string;

   /**
    * The children to be split
    */
   children: React.ReactChild[];

   /**
    * This callback is invoked when a drag starts.
    */
   onDragStarted?: () => void;

   /**
    * This callback is invoked with the current drag during a drag event. It is recommended that it
    * is wrapped in a debounce function.
    *
    * @param sizes - The sizes of each child panel
    */
   onChange?: (sizes: number[]) => void;

   /**
    * This callback is invoked when a drag ends.
    *
    * @param sizes - The sizes of each child panel
    */
   onDragFinished?: (sizes: number[]) => void;
}

export const SplitPane = React.memo((props: SplitPaneProps) => {
   const options = { ...defaultProps, ...props };
   const { split, className, resizerClassName, defaultSizes, style: styleProp } = options;

   const defaultSizesMap = useMemo(()=>(
      defaultSizes?.map((defaultSize)=>getDefaultSizeAsNumber(defaultSize))
   ),[defaultSizes]);

   const defaultSizesIsPixelSizeMap = useMemo(()=>(
      defaultSizes?.map((defaultSize)=>isPixelSize(defaultSize))
   ),[defaultSizes]);

   const splitPanelResizeOptions = useMemo(() => {
      return {
         ...options,
         defaultSizes: defaultSizesMap,
         defaultSizesIsPixelSize: defaultSizesIsPixelSizeMap,
      };
   },[defaultSizesMap, defaultSizesIsPixelSizeMap, options]);

   const { childPanes, resizeState, handleDragStart } = useSplitPaneResize(splitPanelResizeOptions);

   const splitStyleProps: React.CSSProperties = useMemo(()=>(
      split === 'vertical'
      ? {
            left: 0,
            right: 0,
            flexDirection: 'row',
         }
      : {
            bottom: 0,
            top: 0,
            flexDirection: 'column',
            minHeight: '100%',
            width: '100%',
         }),[split]);

   const style: React.CSSProperties = useMemo(()=>({
      display: 'flex',
      flex: 1,
      height: '100%',
      position: 'absolute',
      outline: 'none',
      overflow: 'hidden',
      ...splitStyleProps,
      ...styleProp
   }),[splitStyleProps, styleProp]);

   const classes = ['SplitPane', split, className].join(' ');

   const dragLayerClasses = ['DragLayer', split, resizeState ? 'resizing' : ''].join(' ');

   const entries: React.ReactNode[] = [];

   childPanes.forEach(({ key, node, ref, size, minSize, maxSize }, index) => {
      const indexMinusOne = index - 1;
      const resizing = resizeState && resizeState.index === indexMinusOne;

      if (index !== 0) {
         const resizerDisabled = props.disableResizers !== undefined && ((Array.isArray(props.disableResizers) && ((props.disableResizers as number[]).indexOf(indexMinusOne) >= 0)) || ((props.disableResizers as number)===indexMinusOne));

         entries.push(
            <Resizer
               key={'resizer.' + index}
               split={split}
               className={
                  (resizing ? ' resizing' : '') +
                  (resizerClassName ? ` ${resizerClassName}` : ``)
               }
               index={indexMinusOne}
               disabled={resizerDisabled}
               onDragStarted={handleDragStart}
            />,
         );
      }

      entries.push(
         <Pane
            key={'pane.' + key}
            forwardRef={ref}
            size={size}
            sizeIsPixelSize={defaultSizes ? isPixelSize(defaultSizes[index]) : false}
            minSize={minSize}
            maxSize={maxSize}
            split={split}
            className={classnames(className ? className+"-pane-"+(index+1) : undefined, { 'pane-is-resizing': resizing }) }
         >
            {node}
         </Pane>,
      );
   });

   return (
      <div className={classes} style={style}>
         <div className={dragLayerClasses} style={dragLayerStyle} />
         {entries}
      </div>
   );
});
SplitPane.displayName = 'SplitPane';

const defaultProps = {
   split: 'vertical' as const,
   className: '',
};

const dragLayerStyle: React.CSSProperties = {
   position: 'absolute',
   top: 0,
   right: 0,
   bottom: 0,
   left: 0,
};

const isPixelSize = (defaultSize: number | string):boolean => {
   return (defaultSize === 'layout' || typeof defaultSize === 'string' && (""+defaultSize).endsWith('px'));
};

const getDefaultSizeAsNumber = (defaultSize: number | string):number => {
   if (defaultSize === 'layout') {
      return -1;
   }
   else if (typeof defaultSize === 'string') {
      const size = parseInt(defaultSize as string,10);  // parseInt will strip off the px, which is what we want
      return isNaN(size) ? 1 : size;  // If it still not a number, return 1.  Otherwise, return size.

   }
   return defaultSize as number;
};
